import {KeyboardArrowUp} from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import {format} from "date-fns";
import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import docotorImg from "../../assets/doctor.jpg";
import {setStep} from "../../reducers/appointmentInfoSlice";
import {setDoctor, setService, setSession} from "../../reducers/placeAppointmentSlice";
import palette from "../../theme/palette";
import getOriginalDateTime from "../../utils/formatAMPM";
import DoctorDetailsPopUp from "./DoctorDetailsPopUp";
import {setConsultationFee} from "../../reducers/consultationFeeSlice";

const SelectDate = styled("div")(
  `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 5px;
  width: 50px;
  border: 1px solid #ccc;
  :hover {
    cursor: pointer;
    box-shadow: 0px 0px 5px 1px rgba(61, 61, 61, 0.3);
  }
`,
  (props) => ({
    backgroundColor: props.selected ? palette.light.primary.main : null,
    color: props.selected ? "white" : "black",
  })
);

const ViewMoreButtonContainer = styled("div")(
  `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  width: 50px;
  :hover {
    cursor: pointer;
    box-shadow: 0px 0px 5px 1px rgba(61, 61, 61, 0.3);
  }
`
);

const SelectTime = styled("div")(
  `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  padding: 5px;
  border-radius: 5px;
  :hover {
    cursor: pointer;
    box-shadow: 0px 0px 5px 1px rgba(61, 61, 61, 0.3);
  }
`,
  (props) => ({
    backgroundColor: props.selected ? palette.light.primary.main : null,
    color: props.selected ? "white" : "black",
  })
);

const ErrorText = styled(Typography)({
  color: "red",
  fontSize: "0.8rem",
  paddingLeft: "5px",
});

const getSessionTypeLabel = (type) => {
    switch (type) {
        case 1:
            return "Virtual";
        case 2:
            return "Physical";
        case 3:
            return "Both";
        case 4:
            return "N/A";
        default:
            return "Unknown";
    }
};

const DoctorCard = ({ doctor, selectFirstAvailableSession }) => {
  const dispatch = useDispatch();
  const [viewMore, setViewMore] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [dateNotSelected, setDateNotSelected] = useState(false);
  const [timeNotSelected, setTimeNotSelected] = useState(false);
  const doctorImageLink = `${process.env.REACT_APP_VC_DOMAIN}${process.env.REACT_APP_IMAGE_PATH}${doctor.doctorId}.png`;
    const [selectedService, setSelectedService] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [doctorSessionCategory, setDoctorSessionCategory] = useState([]);
  const [selectedType, setSelectedType] = React.useState("NO_SELECTION");
    const [services, setServices] = useState([]);
    const [hasAvailableSessions, setHasAvailableSessions] = useState(false);

  const onClickChannel = async () => {
      console.log(doctor)
    setViewMore((prev) => !prev);
  };

    const fetchService = useCallback(() => {
        const uniqueServicesMap = new Map();
        for (const session of doctor?.sessions || []) {
            // Only process sessions that match the selected type
            if (String(session.type) === String(selectedType)) {
                for (const service of session.sessionServices || []) {
                    if (!uniqueServicesMap.has(service.ServiceId)) {
                        uniqueServicesMap.set(service.ServiceId, service);
                    }
                }
            }
        }
        setServices(Array.from(uniqueServicesMap.values()));
    }, [doctor?.sessions, selectedType]);

    useEffect(() => {
        fetchService();
    }, [fetchService, selectedType]); // Re-run when selectedType changes

    useEffect(() => {

    }, []);

    const sessions = doctor?.sessions || [];

    const sessionTypes = Array.from(
        new Map(
            sessions.map((session) => [session.type, getSessionTypeLabel(session.type)])
        ).entries()
    ).map(([value, label]) => ({value, label}));

    // const sessionTypes = [{value: 1, label: "Virtual"}, {value: 2, label: "Physical"}, {value: 3, label: "Both"}, {value: 4, label: "N/A"}];          

    useEffect(() => {
      if (selectedType !== "NO_SELECTION") return;
      if (sessionTypes.length !== 1) return;

      setSelectedType(String(sessionTypes[0].value));
      setSelectedService(null);
      setSelectedSession("");
      setSelectedDate("");
    }, [selectedType, sessionTypes]);

     

    // Check if selected services has available sessions
    const checkServiceSessions = useCallback((serviceId) => {
        const hasSessionsForService = sessions.some(session =>
            session.sessionServices?.some(service =>
                service.ServiceId === parseInt(serviceId)
            )
        );
        setHasAvailableSessions(hasSessionsForService);
        if (!hasSessionsForService) {
            setSelectedDate("");
            setSelectedSession("");
        }
        return hasSessionsForService;
    }, [sessions]);


    // Update session dates based on selected services
    const sessionDates = useMemo(() => {
        const dates = {};
        if (!selectedService) return dates;

        sessions.forEach((session) => {
            if (session.sessionServices?.some(service =>
                service.ServiceId === selectedService.ServiceId
            )) {
                const date = session.timeStart.split("T")[0];
                if (!dates[date]) {
                    dates[date] = [session];
                } else {
                    dates[date].push(session);
                }
            }
        });
        return dates;
    }, [sessions, selectedService]);


  const onSelectDate = useCallback(
    (date) => {
      setSelectedDate(date);
      if (selectFirstAvailableSession) {
        setSelectedSession("");
      }
    },
    [setSelectedDate, selectFirstAvailableSession]
  );

  const onSelectTime = useCallback(
    (session) => {
      setSelectedSession(session);
    },
    [setSelectedSession]
  );

  const sessionsAvailable = useMemo(
    () => doctor?.sessions.length > 0,
    [doctor]
  );

  const sessionAvailability = useCallback(
    (doctor, fontSize) => {
      if (sessionsAvailable) {
        return (
          <Typography
            sx={{
              ml: 2,
              fontSize: fontSize ? fontSize : "0.7rem",
              color: "green",
            }}
          >
            Available on {doctor?.sessions[0]?.timeStart.split("T")[0]}
          </Typography>
        );
      } else {
        return (
          <Typography
            sx={{
              ml: 2,
              fontSize: fontSize ? fontSize : "0.7rem",
              color: "red",
            }}
          >
            No sessions available
          </Typography>
        );
      }
    },
    [sessionsAvailable]
  );


  const onClickBookAppointment = useCallback(() => {
      console.log(selectedService)
    if (selectedDate === "") {
      setDateNotSelected(true);
      return;
    } else {
      setDateNotSelected(false);
    }
    if (selectedSession === "" && selectedDate !== "") {
      setTimeNotSelected(true);
      return;
    } else {
      setTimeNotSelected(false);
    }
    dispatch(setDoctor(doctor));
    dispatch(setSession(selectedSession));
    dispatch(setStep({ step: 1 }));
      dispatch(setService(selectedService))
      const totalFee = selectedService.ServiceFee + selectedService.DoctorFee + selectedService.HospitalFee;
      dispatch(setConsultationFee({totalFee: totalFee}))
  }, [dispatch, doctor, selectedSession, selectedDate]);


  const DateNotSelected = dateNotSelected ? (
    <ErrorText>* select a date to continue</ErrorText>
  ) : (
    <></>
  );

  const TimeNotSelected = timeNotSelected ? (
    <ErrorText>* select a time to continue</ErrorText>
  ) : (
    <></>
  );

  useEffect(() => {
    if (sessionsAvailable && selectFirstAvailableSession) {
      const selectedDate = Object.keys(sessionDates)[0];
      setSelectedDate(selectedDate);
      setSelectedSession(
        sessionDates[selectedDate] ? sessionDates[selectedDate][0] : ""
      );
    }
  }, [selectFirstAvailableSession, sessionDates]);


  return (
    <Box width="100%" p={0.5}>
      <DoctorDetailsPopUp
        doctor={doctor}
        showDetails={showDetails}
        setShowDetails={setShowDetails}
        docotorImg={docotorImg}
        doctorImageLink={doctorImageLink}
        sessionAvailability={sessionAvailability}
      />
      <Box
        p={1}
        sx={{
          borderRadius: 1.5,
          boxShadow: "0px 0px 5px 1px rgba(61, 61, 61, 0.15)",
          "&:hover": {
            boxShadow: "0px 0px 8px 1px rgba(61, 61, 61, 0.30)",
            cursor: "pointer",
          },
        }}
      >
        <Box display={"flex"} justifyContent="start" alignItems={"start"}>
          <Box
            display={"flex"}
            flexGrow={1}
            onClick={() => {
              setShowDetails(true);
            }}
          >
            <img
              src={doctorImageLink}
              alt=""
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = docotorImg;
              }}
              style={{ borderRadius: "50%", width: "60px", height: "60px" }}
            />

            <Box display={"flex"} flexDirection={"column"} flexGrow={1}>
              <Typography
                sx={{
                  mt: 1,
                  ml: 2,
                  fontWeight: "bold",
                  flexGrow: 1,
                  fontSize: "13px",
                }}
              >
                {doctor.title + " " + doctor.firstName + " " + doctor.lastName}
              </Typography>
              <Typography
                sx={{
                  ml: 2,
                  fontSize: "0.7rem",
                  wordWrap: "break-word",
                  maxWidth: "150px",
                }}
              >
                {doctor.specialization}
              </Typography>
                {/*{sessionAvailability(doctor)}*/}
            </Box>
          </Box>
          {viewMore ? (
            <IconButton title="Hide details" onClick={onClickChannel}>
              <KeyboardArrowUp />
            </IconButton>
          ) : (
            <Button
              variant="text"
              sx={{
                alignSelf: "center",
                mt: 1,
                borderRadius: 5,
                boxShadow: "none",
                fontSize: "0.75rem",
                fontWeight: "bold",
                whiteSpace: "nowrap",
              }}
              size="small"
              disabled={!sessionsAvailable}
              onClick={onClickChannel}
            >
              Book now
            </Button>
          )}
        </Box>
        {viewMore && (
          <Box>
              <Grid container>
                  <Grid item xs={12}>
                      <Box>
                          <Typography sx={{fontSize: "0.8rem", mt: 1}}>
                              Session Type
                          </Typography>

                          <FormControl
                              fullWidth
                              sx={{
                                  "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                  },
                                  my: 0.5,
                                  border: `1px solid transparent`,
                              }}
                          >
                              {isLoading ? (
                                  <Box
                                      sx={{
                                          width: "100%",
                                          display: "flex",
                                          justifyContent: "center",
                                          my: 1,
                                      }}
                                  >
                                      <CircularProgress size={20}/>
                                  </Box>
                              ) : (
                                    <Autocomplete
                                      disablePortal
                                      options={sessionTypes}
                                      getOptionLabel={(option) => option.label}
                                      value={
                                        selectedType === "NO_SELECTION"
                                          ? null
                                          : sessionTypes.find(
                                            (type) => String(type.value) === String(selectedType)
                                          ) || null
                                      }
                                      onChange={(event, newValue) => {
                                        setSelectedType(newValue ? String(newValue.value) : "NO_SELECTION");
                                        setSelectedService(null);
                                        setSelectedSession("");
                                        setSelectedDate("");
                                      }}
                                      disableClearable
                                      size="small"
                                      sx={{
                                        "& .MuiAutocomplete-input": {
                                          padding: "8px 8px !important",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                          padding: "1px 8px !important",
                                        },
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Please select"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "& fieldset": {
                                                borderColor: palette.light.grey[400],
                                              },
                                              "&:hover fieldset": {
                                                borderColor: palette.light.grey[400],
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: palette.light.primary.main,
                                              },
                                            },
                                          }}
                                        />
                                      )}
                                    />

                              )}
                          </FormControl>
                      </Box>

                  </Grid>
                  {/*session category*/}
                  <Grid item xs={12}>
                      <Box>
                          <Typography sx={{fontSize: "0.8rem", mt: 1}}>
                              Session service
                          </Typography>

                          <FormControl
                              fullWidth
                              sx={{
                                  "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                  },
                                  my: 0.5,
                                  border: `1px solid transparent`,
                              }}
                          >
                              {isLoading ? (
                                  <Box
                                      sx={{
                                          width: "100%",
                                          display: "flex",
                                          justifyContent: "center",
                                          my: 1,
                                      }}
                                  >
                                      <CircularProgress size={20}/>
                                  </Box>
                              ) : (
                                  <Autocomplete
                                      disablePortal
                                      options={services}
                                      getOptionLabel={(option) => option.ServiceName}
                                      value={selectedService}
                                      onChange={(event, newValue) => {
                                          setSelectedService(newValue);
                                          if (newValue) {
                                              checkServiceSessions(newValue.ServiceId);
                                          }
                                      }}
                                      disabled={selectedType === "NO_SELECTION"}
                                      size="small"
                                      sx={{
                                          '& .MuiAutocomplete-input': {
                                              padding: '8px 8px !important',
                                          },
                                          '& .MuiOutlinedInput-root': {
                                              padding: '1px 8px !important',
                                          },
                                      }}
                                      renderInput={(params) => (
                                          <TextField
                                              {...params}
                                              placeholder={
                                                  selectedType === "NO_SELECTION"
                                                      ? "Please select session type first"
                                                      : "Please select services"
                                              }
                                              sx={{
                                                  "& .MuiOutlinedInput-root": {
                                                      "& fieldset": {
                                                          borderColor: palette.light.grey[400],
                                                      },
                                                      "&:hover fieldset": {
                                                          borderColor: palette.light.grey[400],
                                                      },
                                                      "&.Mui-focused fieldset": {
                                                          borderColor: palette.light.primary.main,
                                                      },
                                                  },
                                              }}
                                          />
                                      )}
                                  />
                              )}
                          </FormControl>
                      </Box>
                  </Grid>

              </Grid>

              {selectedService && (
                  <>
                      <Box
                          display={"flex"}
                          justifyContent={"start"}
                          alignItems={"center"}
                          mt={1}
                      >
                          <Typography>Select date</Typography> {DateNotSelected}
                      </Box>

                      <Box
                          display={"flex"}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                          my={1}
                          gap={1}
                      >
                          {!hasAvailableSessions ? (
                              <Typography
                                  component={"div"}
                                  flexGrow={1}
                                  sx={{fontSize: "0.8rem", color: "red"}}
                              >
                                  No sessions available for selected service
                              </Typography>
                          ) : (
                              Object.keys(sessionDates).map((date) => (
                                  <SelectDate
                                      key={date}
                                      onClick={() => onSelectDate(date)}
                                      selected={selectedDate === date}
                                  >
                                      <p>{format(new Date(date), "MMM")}</p>
                                      <p>{format(new Date(date), "d")}</p>
                                  </SelectDate>
                              ))
                          )}
                      </Box>

                      <Box
                          display={"flex"}
                          justifyContent={"start"}
                          alignItems={"center"}
                          mt={1}
                      >
                          <Typography>Select time</Typography>
                          {TimeNotSelected}
                      </Box>

                      <Box
                          display={"flex"}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                          mt={1}
                          gap={1}
                      >
                          {!selectedDate ? (
                              <Typography
                                  component={"div"}
                                  flexGrow={1}
                                  sx={{fontSize: "0.8rem", color: "red"}}
                              >
                                  Please select a date
                              </Typography>
                          ) : (
                              sessionDates[selectedDate]?.map((session) => {
                                  return (
                                      <SelectTime
                                          key={session.timeStart}
                                          onClick={() => onSelectTime(session)}
                                          selected={selectedSession === session}
                                      >
                                          <p>
                                              {format(
                                                  new Date(getOriginalDateTime(session.timeStart)),
                                                  "p"
                                              )}{" "}
                                              -{" "}
                                              {format(
                                                  new Date(getOriginalDateTime(session.timeEnd)),
                                                  "p"
                                              )}
                                          </p>
                                      </SelectTime>
                                  )
                              })
                          )}
                      </Box>
                  </>
              )}

              <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{mt: 2}}
                  onClick={onClickBookAppointment}
                  disabled={!selectedDate || !selectedSession}
              >
                  Book Appointment
              </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DoctorCard;
